import { useEffect, useRef } from 'react';
import type {
  BaseComponentProps,
  ContentfulLinkType,
  ContentfulNavigationType,
} from '@bedrock/core/contentful';
import { Button } from '@bedrock/ui/button';
import { ArrowLeft } from 'lucide-react';
import FeaturedLinks from './featured-links';
import NavigationItem from './navigation-item';
import UtilityLinks from './utility-links';

export type NavigationPageLevel = {
  id: string;
  title: string;
  items: (ContentfulLinkType | ContentfulNavigationType | null)[];
  utility?: (ContentfulLinkType | ContentfulNavigationType)[];
  featuredLinks?: (ContentfulLinkType | ContentfulNavigationType)[];
};

type NavigationLevelProps = {
  level: NavigationPageLevel;
  onEnter: (item: ContentfulNavigationType) => void;
  onBack: () => void;
  isInView: boolean;
  breadcrumbs: ContentfulNavigationType[];
  pathname: string;
  hash: string;
  parentExcludedPaths?: Set<string>;
  title?: string;
} & Pick<BaseComponentProps, 'dictionary' | 'locale'>;

export default function NavigationLevel({
  level,
  onEnter,
  onBack,
  isInView,
  breadcrumbs,
  locale,
  pathname,
  hash,
  parentExcludedPaths,
  title,
}: Readonly<NavigationLevelProps>) {
  const breadcrumbRef = useRef<HTMLButtonElement>(null);
  const firstItemRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!isInView) return;

    if (level.id === 'root') {
      firstItemRef.current?.focus();
    }

    const id = setTimeout(() => {
      if (breadcrumbs.length > 0) {
        breadcrumbRef.current?.focus();
      } else {
        firstItemRef.current?.focus();
      }
    }, 300);

    return () => {
      clearTimeout(id);
    };
  }, [isInView, breadcrumbs, level.id]);

  return (
    <div className="patterns-navigation-hamburger-level h-full w-full flex-none">
      <div
        className="patterns-navigation-hamburger-level-content gap-2xl px-md pb-4xl pt-xl sm:pl-xl sm:pr-2xl xl:pl-3xl flex h-full flex-col overflow-y-auto"
        tabIndex={-1}
      >
        {breadcrumbs.length > 0 && isInView ? (
          <Button
            ref={breadcrumbRef}
            className="patterns-navigation-hamburger-back py-2xs px-sm gap-md br-type-functional-md sm:br-type-functional-lg hover:gap-xs hover:pr-lg active:text-foreground w-fit rounded border-none hover:border-none hover:bg-transparent active:border-none active:bg-transparent motion-safe:transition-[padding,gap] motion-safe:duration-300"
            onClick={onBack}
            size="unset"
            tabIndex={0}
            variant="ghost"
          >
            <ArrowLeft aria-hidden="true" className="size-4" focusable="false" />
            <span>{title}</span>
          </Button>
        ) : null}

        <ul className="patterns-navigation-hamburger-items gap-2xs flex flex-col">
          {level.items.map((item, index) =>
            item ? (
              <NavigationItem
                // eslint-disable-next-line react/no-array-index-key -- Contentful may return duplicate sysId entries
                key={`${item.sysId}-${index}`}
                ref={index === 0 ? firstItemRef : undefined}
                hash={hash}
                isInView={isInView}
                isParentLink={level.id === 'root'}
                item={item}
                locale={locale}
                onEnter={onEnter}
                parentExcludedPaths={parentExcludedPaths}
                pathname={pathname}
              />
            ) : null,
          )}

          <FeaturedLinks featuredLinks={level.featuredLinks} isInView={isInView} locale={locale} />

          <UtilityLinks breadcrumbs={breadcrumbs} locale={locale} utilityLinks={level.utility} />
        </ul>
      </div>
    </div>
  );
}
